import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../api/auth/store';
import { Stack, Form, Button } from 'react-bootstrap'; // Added Form for radio buttons
import MpinInput from '../../components/Auth/MpinInput'; // Import MpinInput
import { getReauthPasskeyAssertion, checkPasskeySupport } from '../../api/auth/passkey_actions';

// Assuming MFA_PIN_LENGTH is known or fetched. For now, hardcoding based on backend constant.
const MFA_PIN_LENGTH = 6; 
const CREDENTIAL_TYPE_PASSWORD = "password";
const CREDENTIAL_TYPE_MPIN = "mpin";
const CREDENTIAL_TYPE_PASSKEY = "passkey";


interface ReauthProps {
  error?: string;
  onConfirm?: (credentialType:string,credentialValue:string) => void;
}

const Reauth: React.FC<ReauthProps> = ({error= "", onConfirm}) => {
  const [password, setPassword] = useState('');
  const [mpin, setMpin] = useState('');
  const [authType, setAuthType] = useState(CREDENTIAL_TYPE_PASSWORD); // 'password' or 'mpin' or 'passkey'
  const [loading, setLoading] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [currentError, setCurrentError] = useState(error);
  const [showAuthOptions, setShowAuthOptions] = useState(false); // Toggle for showing auth options
  const { mfaSettings, signout } = useAuth();
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [passkeySupport] = useState(() => checkPasskeySupport());
  useEffect( () => {
    if (error !== "" && currentError === "")
      setCurrentError(error)
  }, [error])

  useEffect(() => {
    if (mfaSettings) {
      // Prefer passkey if available and supported, then MPIN, then password
      if (mfaSettings.isPasskeyEnabled && mfaSettings.hasPasskeySetup && passkeySupport.supported) {
        setAuthType(CREDENTIAL_TYPE_PASSKEY);
      } else if (mfaSettings.isPinEnabled && mfaSettings.hasPinSetup) {
        setAuthType(CREDENTIAL_TYPE_MPIN);
      }
    }
  }, [mfaSettings, passkeySupport.supported])

  // Focus management effect
  useEffect(() => {
    if (authType === CREDENTIAL_TYPE_PASSWORD && passwordInputRef.current) {
      // Small delay to ensure the input is rendered
      const timer = setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authType])

  // Triggers the system passkey prompt and forwards the assertion to the reauth endpoint.
  // isAuto = fired automatically (on mount/selection); errors are kept quiet so we can fall
  // back to the manual button on browsers that require a user gesture for WebAuthn.
  const triggerPasskey = async (isAuto: boolean) => {
    if (loading) return;
    setLoading(true);
    setCurrentError('');
    try {
      const assertion = await getReauthPasskeyAssertion();
      onConfirm!(CREDENTIAL_TYPE_PASSKEY, assertion);
    } catch (err: any) {
      if (!isAuto) {
        setCurrentError(err?.message || 'Passkey authentication failed. Please try again.');
      }
      console.error('Passkey reauthentication error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (value:string) => {
    setLoading(true);
    setCurrentError('');

    try {
      // Handle traditional authentication
      let credentialValue = '';
      let credentialType = '';

      if (authType === CREDENTIAL_TYPE_PASSWORD) {
        credentialValue = password;
        credentialType = CREDENTIAL_TYPE_PASSWORD;
      } else if (authType === CREDENTIAL_TYPE_MPIN) {
        credentialValue = value;
        credentialType = CREDENTIAL_TYPE_MPIN;
        if (credentialValue.length !== MFA_PIN_LENGTH) {
          setCurrentError(`MPIN must be ${MFA_PIN_LENGTH} digits.`);
          setLoading(false);
          return;
        }
      }

      if (!credentialValue) {
          setCurrentError('Please enter your credentials.');
          setLoading(false);
          return;
      }

      
      setCurrentError("")
      onConfirm!(credentialType, credentialValue);
    } catch (err: any) {
      const errorMessage = err.response?.data?.Msg || err.response?.data?.Code || err.message || 'An unexpected error occurred.';
      setCurrentError(errorMessage);
      console.error('Reauthentication error:', err);
    } finally {
      setLoading(false);
    }
  };
  //console.log(currentError)
  const showmpin = mfaSettings && mfaSettings.isPinEnabled && mfaSettings.hasPinSetup
  const showPasskey = mfaSettings && mfaSettings.isPasskeyEnabled && mfaSettings.hasPasskeySetup && passkeySupport.supported;
  const showOptions = showmpin || showPasskey;

  // Automatically prompt the system passkey when it becomes the active method. Fires once per
  // selection; resets when the user switches to another method so re-selecting re-triggers it.
  const autoPasskeyTriggered = useRef(false);
  useEffect(() => {
    if (authType !== CREDENTIAL_TYPE_PASSKEY) {
      autoPasskeyTriggered.current = false;
      return;
    }
    if (showPasskey && !loading && !autoPasskeyTriggered.current) {
      autoPasskeyTriggered.current = true;
      triggerPasskey(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authType, showPasskey]);
  //console.log(showOptions, mfaSettings)
  
  // Helper function to get user display name
  const getUserDisplayName = () => {
    //console.log("Session Details:", mfaSettings);
    if (mfaSettings?.display) return mfaSettings.display;
    return 'User';
  };

  // Helper function to get initials for avatar fallback
  const getInitials = () => {
    const displayName = getUserDisplayName();
    const names = displayName.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  };

  return (
    <div className="reauth-container" style={{ position: 'relative', minHeight: '400px' }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        {mfaSettings?.avatar ? (
          <img 
            src={mfaSettings.avatar} 
            alt="Profile" 
            style={{ 
              width: '80px', 
              height: '80px', 
              borderRadius: '50%', 
              objectFit: 'cover',
              marginBottom: '12px'
            }} 
          />
        ) : (
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#007bff',
            color: 'gray',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 'bold',
            marginBottom: '12px'
          }}>
            {getInitials()}
          </div>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <h5 style={{ 
            margin: 0, 
            fontWeight: 'normal',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '300px',
            fontSize: '1.1rem'
          }}>
            Welcome back, <strong>{getUserDisplayName()}</strong>
          </h5>
          {showOptions && (
            <button
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#ababab'
              }}
              onClick={() => setShowAuthOptions(!showAuthOptions)}
              type="button"
              aria-label="Toggle authentication options"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                style={{
                  transform: showAuthOptions ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
              </svg>
            </button>
          )}
        </div>
      </div>
  
        {showOptions && showAuthOptions && <Form.Group className="mb-3">
          {showPasskey && <Form.Check
            inline
            type="radio"
            label="Passkey"
            name="authType"
            id="authTypePasskey"
            value={CREDENTIAL_TYPE_PASSKEY}
            checked={authType === CREDENTIAL_TYPE_PASSKEY}
            onChange={() => { setAuthType(CREDENTIAL_TYPE_PASSKEY); setCurrentError(''); setPassword(''); setMpin(''); }}
          />}

          {showmpin && <Form.Check
            inline
            type="radio"
            label="MPIN"
            name="authType"
            id="authTypeMpin"
            value={CREDENTIAL_TYPE_MPIN}
            checked={authType === CREDENTIAL_TYPE_MPIN}
            onChange={() => { setAuthType(CREDENTIAL_TYPE_MPIN); setCurrentError(''); setPassword(''); setMpin(''); }}
          />}

          <Form.Check
            inline
            type="radio"
            label="Password"
            name="authType"
            id="authTypePassword"
            value={CREDENTIAL_TYPE_PASSWORD}
            checked={authType === CREDENTIAL_TYPE_PASSWORD}
            onChange={() => {
              setAuthType(CREDENTIAL_TYPE_PASSWORD);
              setCurrentError('');
              setPassword('');
              setMpin('');
              // Focus will be handled by useEffect
            }}
          />
        </Form.Group>}

        <Stack direction='horizontal' gap={2} style={{top: '20px', position: 'relative'}}>
          {authType === CREDENTIAL_TYPE_PASSWORD && <>
            <div className="form-group" style={{ flexGrow: 1, marginBottom: '0px' }}>
              <Form.Control
                ref={passwordInputRef}
                type="password"
                id="password"
                value={password}
                placeholder='Password'
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !loading && password.trim()) {
                    e.preventDefault();
                    handleSubmit(password);
                  }
                }}
                required
                disabled={loading}
                autoFocus
              />
            </div>
          
            <Button type="submit" disabled={loading} onClick={() => handleSubmit(password)} variant="primary">
              {loading ? 'Verifying...' : 'Verify'}
            </Button>
          </>
          }

          {authType === CREDENTIAL_TYPE_MPIN && (
            <div style={{ flexGrow: 1 }}>
              <MpinInput
                enabled={!loading}
                pinLength={MFA_PIN_LENGTH}
                onChange={(pin) => {
                  setMpin(pin)
                  setCurrentError("")
                }}
                onFinish={(pin) => {
                  setMpin(pin); // Ensure mpin state is updated
                  // Automatically submit when the MPIN is fully entered
                  // This will trigger the handleSubmit logic
                  handleSubmit(pin);
                }}
                label="" // No separate label, placeholder is part of MpinInput or can be added
              />
            </div>
          )}

          {authType === CREDENTIAL_TYPE_PASSKEY && (
            <div style={{ flexGrow: 1, textAlign: 'center' }}>
              <div style={{
                padding: '20px',
                border: '2px dashed #007bff',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🔐</div>
                <p style={{ margin: '0 0 10px 0', color: '#6c757d' }}>
                  {loading ? 'Waiting for your passkey…' : 'Confirm with your device passkey to continue'}
                </p>
                <Button
                  variant="primary"
                  disabled={loading}
                  onClick={() => triggerPasskey(false)}
                  style={{ minWidth: '120px' }}
                >
                  {loading ? 'Authenticating...' : 'Use Passkey'}
                </Button>
              </div>
            </div>
          )}
          
        </Stack>
        {currentError && <p className="error-message" style={{color: 'red', marginTop: '30px'}}>{currentError}</p>}
        
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center'
        }}>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#2196F3',
              padding: '8px 16px',
              cursor: signingOut ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              textDecoration: 'underline',
              opacity: signingOut ? '0.5' : '1'
            }}
            onMouseEnter={(e) => {
              if (!signingOut) e.currentTarget.style.opacity = '0.7';
            }}
            onMouseLeave={(e) => {
              if (!signingOut) e.currentTarget.style.opacity = '1';
            }}
            onClick={async () => {
              if (signingOut) return;
              
              setSigningOut(true);
              try {
                const result = await signout();
                if (result === "200" || result === "session_invalid") {
                  window.location.reload();
                }
              } catch (error) {
                console.error("Signout error:", error);
                setSigningOut(false);
              }
            }}
            type="button"
            disabled={signingOut}
          >
            {signingOut ? 'Signing out...' : 'Sign Out'}
          </button>
          <div style={{ marginTop: '8px' }}>
            <a
              href="/forgot"
              style={{
                color: '#6c757d',
                fontSize: '13px',
                textDecoration: 'none',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
                e.currentTarget.style.color = '#007bff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none';
                e.currentTarget.style.color = '#6c757d';
              }}
            >
              Forgot Password?
            </a>
          </div>
        </div>
    </div>
  );
};

export default Reauth;
